import angular from "angular";
import kendo from "@progress/kendo-ui"; import "kendo.timezones";

import ngSanitize from "angular-sanitize";
import ngAnimate from "angular-animate";
import ngAria from "angular-aria";
import uiBootstrap from "angular-ui-bootstrap";
import kendoDirectives from "../../shims/kendoDirectives";

import EventRangeFilterModule from "./EventRangeFilter";
import AllEventTypesServiceModule from "../../services/AllEventTypesService";
import AllStatesServiceModule from "../../services/AllStatesService";
import AllTrackingStatusesServiceModule from "../../services/AllTrackingStatusesService";
import AllOfficeTypesServiceModule from "../../services/AllOfficeTypesService";
import TrackerDataServiceModule from "../../services/TrackerDataService";
import TrackingTargetDataServiceModule from "../../services/TrackingTargetDataService";

import Assert from "tbAssert";
import EventFilter from "../../models/EventFilter";
import EventReportedRangeFilterAdapter from "./EventReportedRangeFilterAdapter";

const mScopeSvc = new WeakMap();
const mLogSvc = new WeakMap();
const mQSvc = new WeakMap();
const mTimeoutSvc = new WeakMap();

const mEventRangeFilterFactory = new WeakMap();
const mAllEventTypesSvc = new WeakMap();
const mAllTrackingStatusesSvc = new WeakMap();
const mAllStatesSvc = new WeakMap();
const mAllOfficeTypesSvc = new WeakMap();
const mTrackingTargetDataSvc = new WeakMap();
const mTrackerDataSvc = new WeakMap();

const mTrackingTargetDataSource = new WeakMap();
const mEventTrackerDataSource = new WeakMap();

const mEventFilterOptions = new WeakMap();
const mDataLoadedFlag = new WeakMap();

// applyEventFilterFn: "&tbApplyEventFilterFn",
// additionalMainPanelClasses: "@tbAdditionalMainPanelClasses",
// additionalBottomPanelClasses: "@tbAdditionalBottomPanelClasses",
// additionalFilterFormClasses: "@tbAdditionalFilterFormClasses",
// showEventNameFilter: "<tbShowEventNameFilter",
// showEventTypeFilter: "<tbShowEventTypeFilter",
// showEventTargetFilters: "<tbShowEventTargetFilters",
// showEventStatusFilters: "<tbShowEventStatusFilters",
// showEventTrackerFilters: "<tbShowEventTrackerFilters",
// showEventLocationFilters: "<tbShowEventLocationFilters",
// showEventDateRangeFilters: "<tbShowEventDateRangeFilters",
// showEventReportedDateRangeFilters: "<tbShowEventReportedDateRangeFilters",
// useWideLayout: "=tbUseWideLayout"

const mEventFilterData = new WeakMap(); //filter data from the filter panel host. Filter data must be stored here before calling the host apply method.
const mEventFilter = new WeakMap(); //filter data model edited by the user. Does not become active until the user applies it.
const mEventRangeFilter = new WeakMap();
const mEventReportedRangeFilter = new WeakMap();

const mApplyEventFilterFn = new WeakMap();
const mAdditionalMainPanelClasses = new WeakMap();
const mAdditionalBottomPanelClasses = new WeakMap();
const mAdditionalFilterFormClasses = new WeakMap();
const mShowEventNameFilter = new WeakMap();
const mShowEventTypeFilter = new WeakMap();
const mShowEventTargetFilters = new WeakMap();
const mShowEventStatusFilters = new WeakMap();
const mShowEventTrackerFilters = new WeakMap();
const mShowEventLocationFilters = new WeakMap();
const mShowEventDateRangeFilters = new WeakMap();
const mShowEventReportedDateRangeFilters = new WeakMap();
const mUseWideLayout = new WeakMap();

const eventRangeFilterParseFormat = "M/D/YYYY h:mm A";

class EventFilterPanelController {
    constructor(
        $scope,
        $log,
        $q,
        $timeout,
        EventRangeFilter,
        AllTrackingStatuses,
        AllOfficeTypes,
        AllEventTypesService,
        AllStatesService,
        TrackerData,
        TrackingTargetData
    ) {

        Assert.StrictAssert($scope != null, "Assertion Failure: Missing Scope Service Dependency");
        Assert.StrictAssert($log != null, "Assertion Failure: Missing Log Service Dependency");
        Assert.StrictAssert($q != null, "Assertion Failure: Missing Q Service Dependency");
        Assert.StrictAssert($timeout != null, "Assertion Failure: Missing Q Service Dependency");
        Assert.StrictAssert(EventRangeFilter != null, "Assertion Failure: Missing Event Range Filter Dependency");
        Assert.StrictAssert(AllTrackingStatuses != null, "Assertion Failure: Missing All Tracking Statuses Service Dependency");
        Assert.StrictAssert(AllOfficeTypes != null, "Assertion Failure: Missing All Office Types Service Dependency");
        Assert.StrictAssert(AllEventTypesService != null, "Assertion Failure: Missing All Event Types Service Dependency");
        Assert.StrictAssert(AllStatesService != null, "Assertion Failure: Missing All States Service Dependency");
        Assert.StrictAssert(TrackerData != null, "Assertion Failure: Missing Tracker Data Service Dependency");
        Assert.StrictAssert(TrackingTargetData != null, "Assertion Failure: Missing Tracking Target Data Service Dependency");

        mScopeSvc.set(this, $scope);
        mLogSvc.set(this, $log);
        mQSvc.set(this, $q);
        mTimeoutSvc.set(this, $timeout);
        mEventRangeFilterFactory.set(this, EventRangeFilter);
        mAllTrackingStatusesSvc.set(this, AllTrackingStatuses);
        mAllOfficeTypesSvc.set(this, AllOfficeTypes);
        mAllEventTypesSvc.set(this, AllEventTypesService);
        mAllStatesSvc.set(this, AllStatesService);
        mTrackingTargetDataSvc.set(this, TrackingTargetData);
        mTrackerDataSvc.set(this, TrackerData);

        mEventFilter.set(this, new EventFilter({}));

        mDataLoadedFlag.set(this, null);

        $scope.$on("$destroy", () => {
            mLogSvc.get(this).debug("Event Filter Panel: Destroy");

            let tbEventFilterPanelRootElement =
                angular.element("#tb-event-filter-panel-root");

            kendo.destroy(tbEventFilterPanelRootElement);
        });

    }
    
    $onInit() {
        const logSvc = mLogSvc.get(this);
        const qSvc = mQSvc.get(this);

        logSvc.debug("Event Filter Panel Controller: Initializing Filter Widget Options.");

        const EventRangeFilter = mEventRangeFilterFactory.get(this);

        let allAvailableTrackingStatusesList = mAllTrackingStatusesSvc.get(this).AvailableStatuses;
        let allOfficeTypesList = mAllOfficeTypesSvc.get(this).AvailableOfficeTypes;
        let officeTypesDataSource = new kendo.data.DataSource({
            data: allOfficeTypesList,
            sort: {
                field: "sortAs",
                dir: "asc"
            }
        });

        const eventRangeFilterModelOptions = {
            updateOn: "default blur",
            debounce: {
                "default": 500,
                "blur": 0
            }
        };

        const eventRangeFilterPickerOptions = {
            start: {
                format: "M/d/yyyy h:mm tt",
                timeFormat: "h:mm tt",
                interval: 30
            },
            end: {
                format: "M/d/yyyy h:mm tt",
                timeFormat: "h:mm tt",
                interval: 30
            }
        };

        mEventFilterOptions.set(this, {
            eventNameFilterModelOptions: {
                updateOn: "default blur",
                debounce: {
                    "default": 250,
                    "blur": 0
                }
            },
            eventRangeFilterModelOptions: eventRangeFilterModelOptions,
            eventReportedRangeFilterModelOptions: eventRangeFilterModelOptions,
            eventRangeFilterPickerOptions: eventRangeFilterPickerOptions,
            eventReportedRangeFilterPickerOptions: eventRangeFilterPickerOptions,

            includeStatusFilterSelectorOptions: {
                dataSource: allAvailableTrackingStatusesList, //new dataSource obj will be inited from data.
                dataTextField: "trackingStatusName",
                dataValueField: "trackingStatusId"
            },
            excludeStatusFilterSelectorOptions: {
                dataSource: allAvailableTrackingStatusesList, //new dataSource obj will be inited from data.
                dataTextField: "trackingStatusName",
                dataValueField: "trackingStatusId"
            },
            officeTypeSelectorOptions: {
                dataSource: officeTypesDataSource, //new dataSource obj inited from data.
                dataTextField: "officeTypeName",
                dataValueField: "officeTypeId"
            }
        });

        let allEventTypesList = [];
        let allStatesList = [];

        let loadEventTypesPromise = mAllEventTypesSvc.get(this).GetEventTypesAsync()
            .then(
                (eventTypesList) => {
                    allEventTypesList.length = 0;
                    Array.prototype.push.apply(allEventTypesList, eventTypesList);
                    mEventFilterOptions.get(this).includeEventTypeFilterSelectorOptions = {
                        dataSource: allEventTypesList,
                        dataTextField: "eventTypeName",
                        dataValueField: "eventTypeId"
                    };
                    mEventFilterOptions.get(this).excludeEventTypeFilterSelectorOptions = {
                        dataSource: allEventTypesList,
                        dataTextField: "eventTypeName",
                        dataValueField: "eventTypeId"
                    };
                },
                (errorResponse) => {
                    mLogSvc.get(this).error("Error Retrieving Event Type List", errorResponse);
                }
            );

        let loadStatesPromise = mAllStatesSvc.get(this).GetStatesAsync()
            .then(
                (allStatesData) => {
                    allStatesList.length = 0;
                    Array.prototype.push.apply(allStatesList, allStatesData);

                    mEventFilterOptions.get(this).locationStateSelectorOptions = {
                        dataSource: allStatesList, //new dataSource obj inited from data.
                        dataTextField: "fullName",
                        dataValueField: "shortName"
                    };

                    mEventFilterOptions.get(this).officeStateSelectorOptions = {
                        dataSource: allStatesList, //new dataSource obj inited from data.
                        dataTextField: "fullName",
                        dataValueField: "shortName"
                    };

                },
                (errorResponse) => {
                    mLogSvc.get(this).error("State data load failed:", errorResponse);
                }
            );

        let loadTargetDataPromise = qSvc.when([]).then(() => {
            let trackingTargetDataSource = new kendo.data.DataSource({
                serverFiltering: true,
                transport: {
                    read: (options) => {

                        let lnQuery = null;

                        if (options.data["filter"] != undefined) {
                            if (options.data.filter.filters.length > 0) {
                                lnQuery = options.data.filter.filters[0].value;
                            }
                        }

                        if (lnQuery == null) {
                            options.success([]);
                            return;
                        }

                        mTrackingTargetDataSvc.get(this)
                            .query({lastName: lnQuery})
                            .$promise
                            .then(
                                (response) => {
                                    options.success(response.queryResults);
                                },
                                (response) => {
                                    options.error(response);
                                }
                            );
                    }
                }
            });

            mTrackingTargetDataSource.set(this, trackingTargetDataSource);

            mEventFilterOptions.get(this).targetFilterSelectorOptions = {
                dataSource: mTrackingTargetDataSource.get(this), //uses this datasource obj
                filter: "startsWith",
                delay: 500,
                dataTextField: "targetName",
                dataValueField: "trackingTargetId"
            };

            return true;
        });

        let loadTrackerDataPromise = qSvc.when([]).then(() => {
            let eventTrackerDataSource = new kendo.data.DataSource({
                serverFiltering: true,
                transport: {
                    read: (options) => {
                        let lnQuery = null;

                        if (options.data["filter"] != null) {
                            if (options.data.filter.filters.length > 0) {
                                lnQuery = options.data.filter.filters[0].value;
                            }
                        }

                        if (lnQuery == null) {
                            options.success([]);
                            return;
                        }

                        mTrackerDataSvc.get(this).search(lnQuery)
                            .then(
                                (trackerSearchResult) => {
                                    options.success(trackerSearchResult.trackerData);
                                },
                                (errorResponse) => {
                                    options.error(errorResponse);
                                }
                            );
                    }
                }
            });

            mEventTrackerDataSource.set(this, eventTrackerDataSource);

            mEventFilterOptions.get(this).eventTrackerFilterSelectorOptions = {
                dataSource: mEventTrackerDataSource.get(this),
                filter: "contains",
                delay: 500,
                dataTextField: "displayName",
                dataValueField: "trackerId",
                minLength: 1
            };

            return true;
        });

        mEventRangeFilter.set(this, EventRangeFilter({
            eventFilter: this.eventFilter,
            parseFormat: eventRangeFilterParseFormat
        }));
        const eventReportedRangeFilter = EventRangeFilter({
            eventFilter: new EventReportedRangeFilterAdapter({eventFilterSvc: this.eventFilter}),
            parseFormat: eventRangeFilterParseFormat
        });
        mEventReportedRangeFilter.set(this, eventReportedRangeFilter);

        qSvc.all([loadEventTypesPromise, loadStatesPromise, loadTargetDataPromise, loadTrackerDataPromise])
            .then(
                () => {
                    //Load event filter data from filter panel host after we've loaded the backend data.
                    mTimeoutSvc.get(this)(() => {
                        this.loadEventDataFromStore();
                        mDataLoadedFlag.set(this, new Date());
                        logSvc.debug("Event Filter Panel Controller: Initialized. Filter Data Read Out After Loading Follows.");
                        logSvc.debug(this.eventFilter);
                    }, 0);
                },
                () => {
                    mLogSvc.get(this).error("Error loading data for filter");
                }
            );
    }

    _getTargetBatch(trackingTargetIds) {
        if ((trackingTargetIds != null) &&
            (trackingTargetIds.length > 0)) {
            let trackingTargetIdsString = trackingTargetIds.join(",");
            let targetRetrievalPromise =
                mTrackingTargetDataSvc.get(this)
                    .getBatch({
                        targetIds: trackingTargetIdsString
                    })
                    .$promise
                    .then(
                        (retrievedTargets) => {
                            return retrievedTargets.trackingTargets;
                        },
                        (errorResponse) => {
                            return mQSvc.get(this).reject(errorResponse);
                        }
                    );

            return targetRetrievalPromise;
        }
        else {
            return mQSvc.get(this).when([]);
        }
    }

    loadEventDataFromStore() {
        EventFilter.copyFilter(this.eventFilter, this.eventFilterData);
        let currentScope = mScopeSvc.get(this);

        if (currentScope.targetNameSelector != null) {

            this._getTargetBatch(this.eventFilter.targetFilter).then(
                (targets) => {
                    currentScope.targetNameSelector.dataSource.data(targets);
                    currentScope.targetNameSelector.value(this.eventFilter.targetFilter);
                }
            );
        }
        else {
            mLogSvc.get(this).debug("Skipped Loading Target Name Selector Data: Widget Not Found In Current Scope");
        }

        if (currentScope.eventTrackerSelector != null) {
            mTrackerDataSvc.get(this).getTrackerBatch(this.eventFilter.trackerFilter).then(
                (trackers) => {
                    currentScope.eventTrackerSelector.dataSource.data(trackers);
                    currentScope.eventTrackerSelector.value(this.eventFilter.trackerFilter);
                }
            );
        }
        else {
            mLogSvc.get(this).debug("Skipped Loading Event Tracker Selector: Widget Not Found In Current Scope");
        }
    }

    saveEventDataToStore() {
        EventFilter.copyFilter(this.eventFilterData, this.eventFilter);
    }

    get trackingTargetDataSource() {
        return mTrackingTargetDataSource.get(this);
    }

    get eventTrackerDataSource() {
        return mEventTrackerDataSource.get(this);
    }

    get eventFilter() {
        return mEventFilter.get(this);
    }

    get eventRangeFilter() {
        return mEventRangeFilter.get(this);
    }

    get eventReportedRangeFilter() {
        return mEventReportedRangeFilter.get(this);
    }

    get eventFilterOptions() {
        return mEventFilterOptions.get(this);
    }

    get applyEventFilterFn() {
        return mApplyEventFilterFn.get(this);
    }

    set applyEventFilterFn(updatedVal) {
        mApplyEventFilterFn.set(this, updatedVal);
    }

    get additionalMainPanelClasses() {
        return mAdditionalMainPanelClasses.get(this);
    }

    set additionalMainPanelClasses(updatedVal) {
        mAdditionalMainPanelClasses.set(this, updatedVal);
    }

    get additionalBottomPanelClasses() {
        return mAdditionalBottomPanelClasses.get(this);
    }

    set additionalBottomPanelClasses(updatedVal) {
        mAdditionalBottomPanelClasses.set(this, updatedVal);
    }

    get additionalFilterFormClasses() {
        return mAdditionalFilterFormClasses.get(this);
    }

    set additionalFilterFormClasses(updatedVal) {
        mAdditionalFilterFormClasses.set(this, updatedVal);
    }

    get showEventNameFilter() {
        return mShowEventNameFilter.get(this);
    }

    set showEventNameFilter(updatedVal) {
        mShowEventNameFilter.set(this, updatedVal);
    }

    get showEventTypeFilter() {
        return mShowEventTypeFilter.get(this);
    }

    set showEventTypeFilter(updatedVal) {
        mShowEventTypeFilter.set(this, updatedVal);
    }

    get showEventTargetFilters() {
        return mShowEventTargetFilters.get(this);
    }

    set showEventTargetFilters(updatedVal) {
        mShowEventTargetFilters.set(this, updatedVal);
    }

    get showEventStatusFilters() {
        return mShowEventStatusFilters.get(this);
    }

    set showEventStatusFilters(updatedVal) {
        mShowEventStatusFilters.set(this, updatedVal);
    }

    get showEventTrackerFilters() {
        return mShowEventTrackerFilters.get(this);
    }

    set showEventTrackerFilters(updatedVal) {
        mShowEventTrackerFilters.set(this, updatedVal);
    }

    get showEventLocationFilters() {
        return mShowEventLocationFilters.get(this);
    }

    set showEventLocationFilters(updatedVal) {
        mShowEventLocationFilters.set(this, updatedVal);
    }

    get showEventDateRangeFilters() {
        return mShowEventDateRangeFilters.get(this);
    }

    set showEventDateRangeFilters(updatedVal) {
        mShowEventDateRangeFilters.set(this, updatedVal);
    }

    get showEventReportedDateRangeFilters() {
        return mShowEventReportedDateRangeFilters.get(this);
    }

    set showEventReportedDateRangeFilters(updatedVal) {
        mShowEventReportedDateRangeFilters.set(this, updatedVal);
    }

    get useWideLayout() {
        return mUseWideLayout.get(this);
    }

    set useWideLayout(updatedVal) {
        mUseWideLayout.set(this, updatedVal);
    }

    get eventFilterData() {
        return mEventFilterData.get(this);
    }

    set eventFilterData(updatedVal) {
        mEventFilterData.set(this, updatedVal);
    }

    get dataLoaded() {
        return mDataLoadedFlag.get(this);
    }

    // Methods
    applyEventFilter(e) {
        e.preventDefault();

        mLogSvc.get(this).debug("Event Filter Panel: Apply Event Filter");

        mLogSvc.get(this).debug("Event Filter Panel: Host Filter Data Before Save");
        mLogSvc.get(this).debug(this.eventFilterData.toJSON());

        this.saveEventDataToStore();

        mLogSvc.get(this).debug("Event Filter Panel: Host Filter Data After Save");
        mLogSvc.get(this).debug(this.eventFilterData.toJSON());

        this.applyEventFilterFn();
    }

    clearEventFilter(e) {
        let currentEventFilter = this.eventFilter;
        currentEventFilter.nameFilter = "";
        this.eventRangeFilter.clear();
        this.eventReportedRangeFilter.clear();
        currentEventFilter.includeStatusFilter = [];
        currentEventFilter.excludeStatusFilter = [];
        currentEventFilter.typeFilter = [];
        currentEventFilter.hasNoTrackersFilter = false;
        currentEventFilter.trackerFilter = [];
        currentEventFilter.targetFilter = [];
        currentEventFilter.targetFilterRequireAll = false;
        currentEventFilter.locationStateFilter = [];
        currentEventFilter.officeStateFilter = [];
        currentEventFilter.officeTypeFilter = [];

        this.applyEventFilter(e);
    }

    resetEventFilterChanges(e) {
        e.preventDefault();
        this.loadEventDataFromStore();
    }
}

EventFilterPanelController.$inject = [
    "$scope",
    "$log",
    "$q",
    "$timeout",
    "EventRangeFilter",
    "AllTrackingStatuses",
    "AllOfficeTypes",
    "AllEventTypesService",
    "AllStatesService",
    "TrackerData",
    "TrackingTargetData"
];

EventFilterPanelController.dependencies = [
    ngSanitize,
    ngAnimate,
    ngAria,
    uiBootstrap,
    kendoDirectives,
    EventRangeFilterModule,
    AllEventTypesServiceModule,
    AllStatesServiceModule,
    AllTrackingStatusesServiceModule,
    AllOfficeTypesServiceModule,
    TrackerDataServiceModule,
    TrackingTargetDataServiceModule
];

EventFilterPanelController.template = require("./EventFilterPanel.tpl.html");

EventFilterPanelController.controllerAs = "eventFilterPanel";

export default EventFilterPanelController;



//////////////////
// WEBPACK FOOTER
// ./src/views/event-filter-panel/EventFilterPanelController.js
// module id = CIhO
// module chunks = 1